// @flow
import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import radio from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Radio';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

import { commonStyles } from '../commonStyles';

export const createNoAppPartnerProgram = (isLoggedIn: boolean) => ({
  value: 'no-app-partner-program',
  label: 'Zapier Partner Program: Co-Marketing my app and more',
  zendeskCustomFieldValue: 'pcf-new-co-marketing',
  child: radio({
    id: 'noapppartnerprogram',
    label: "Let's get you some help",
    zendeskCustomFieldId: 360044560112,
    items: [
      {
        value: 'new-partner-program',
        label: 'Zapier Partner Program',
        zendeskCustomFieldValue: 'pcf-new-co-marketing-partner-program',
        child: help({
          docs: {
            title: 'Zapier Partner Program',
            body: () => (
              <Fragment>
                <p>
                  The Zapier Integration Partner Program (accessible to public
                  platform partners) and all of its benefits are free. All you
                  have to do is get more users and maintain a healthy
                  integration in order to access benefits!
                </p>
                <ul style={commonStyles.ul}>
                  <li>
                    We’ll share personalized recommendations for growing your
                    integration, based on what’s worked for our top partners.
                  </li>
                  <li>
                    Our team acts as frontline support for your integration,
                    triaging bugs and passing along your users’ feedback.
                  </li>
                  <li>
                    As your integration grows, you’ll gain more benefits from
                    the program.
                  </li>
                </ul>
              </Fragment>
            ),
            links: [
              {
                href: `${ZAPIER_APP_BASE_URL}/platform/partner-program`,
                title: 'Partner Program Overview',
              },
              {
                href: `${BASE_DOC_URL_VB}/publish/partner-program`,
                title: 'About the Partner Program',
              },
              {
                href: `${BASE_DOC_URL_VB}/publish/partner-faq`,
                title: 'Zapier Partner Program tips',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what question you have about the Partner Program.`,
            },
          ],
        }),
      },
      {
        value: 'new-zap-templates',
        label: 'Zap Templates & Embedding for Partners',
        zendeskCustomFieldValue: 'pcf-new-co-marketing-templates',
        child: help({
          docs: {
            title: 'Zap Templates',
            body: () => (
              <Fragment>
                <p>
                  Zap templates are readymade integrations or Zaps with the apps
                  and core fields pre-selected, for publicly available Zapier
                  integrations. In a few clicks, they help people discover a use
                  case, connect apps, and turn on the Zap. Zap templates are the
                  fastest way for your users to automate workflows.
                </p>
                <p>
                  If you’re interested in releasing a public integration, learn
                  about templates and how promoting them can help you grow:
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates`,
                title: 'Zap Templates Guide',
              },
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates#promote-your-zap-templates`,
                title: 'Promote Your Zap Templates',
              },
              {
                href: `${BASE_DOC_URL_VB}/embed/zap-templates`,
                title: 'Embed Zap Templates',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about Zap templates.`,
            },
          ],
        }),
      },
      {
        value: 'new-beta-launch',
        label: 'Zapier Beta & Launch',
        zendeskCustomFieldValue: 'pcf-new-co-marketing-beta-launch',
        child: help({
          docs: {
            title: 'Zapier Beta & Launch',
            // TODO: get bold to take effect in <p> below.
            body: () => (
              <Fragment>
                <p>
                  Apps begin their public life on Zapier as Beta, after clearing
                  app review. During this time our system checks integrations
                  daily to ensure they remain healthy, and that essential launch
                  materials like Zap templates have been created. Once all the
                  requirements are met, you will be automatically enrolled in
                  the launch queue and notified of our co-marketing.
                </p>
                <p>
                  After launch, you will automatically join the{' '}
                  <b>Zapier Partner Program</b> and can access the program’s{' '}
                  <Link
                    href={`${ZAPIER_APP_BASE_URL}/platform/partner-program`}
                    target="_blank"
                  >
                    many co-marketing benefits
                  </Link>
                  .
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/partner-program`,
                title: 'Launch with the Partner Program',
              },
              {
                href: `${BASE_DOC_URL_VB}/publish/public-integration#5-beta-phase`,
                title: 'The Beta Phase',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about Zapier Beta & Launch.`,
            },
          ],
        }),
      },
    ],
  }),
});
